// Utility function to categorize changes for CREATE, UPDATE, and DELETE operations

/**
 * Helper function to check if an object has been modified
 * Compares specific fields between current and original
 */
const hasBeenModified = (current, original, fieldsToCompare) => {
  if (!original) return false;

  return fieldsToCompare.some((field) => {
    const currentValue = current[field];
    const originalValue = original[field];

    // Handle different types of values
    if (currentValue === originalValue) return false;
    if (currentValue === undefined && originalValue === undefined) return false;
    if (currentValue === null && originalValue === null) return false;

    // For strings, also check trimmed values
    if (typeof currentValue === 'string' && typeof originalValue === 'string') {
      return currentValue !== originalValue;
    }

    return true;
  });
};

/**
 * Categorizes event changes
 */
export const categorizeEventChanges = (currentEvent, originalEvent) => {
  const result = {
    event: null,
    scenarios: { create: [], update: [], delete: [] },
    attributes: { create: [], update: [], delete: [] },
    customValues: [],
  };

  // Determine event operation
  if (!currentEvent.event_db_id) {
    // No db_id means this is a new event
    result.event = { operation: 'CREATE', data: currentEvent };
  } else if (!originalEvent) {
    // Has db_id but no original means this is a new event
    result.event = { operation: 'CREATE', data: currentEvent };
  } else if (
    hasBeenModified(currentEvent, originalEvent, ['event_name', 'event_order', 'description'])
  ) {
    // Has db_id and was modified - track which fields changed
    const changedFields = {};

    if (currentEvent.event_name !== originalEvent?.event_name) {
      changedFields.event_name = currentEvent.event_name;
    }
    if (currentEvent.event_order !== originalEvent?.event_order) {
      changedFields.sequence = currentEvent.event_order;
    }
    if (currentEvent.description !== originalEvent?.description) {
      changedFields.description = currentEvent.description;
    }

    result.event = {
      operation: 'UPDATE',
      data: currentEvent,
      changedFields, // Include only the fields that actually changed
    };
  } else {
    // Has db_id but wasn't modified
    result.event = { operation: 'NONE', data: currentEvent };
  }

  // Process scenarios
  const currentScenarios = currentEvent.scenarios || [];
  const originalScenarios = originalEvent?.scenarios || [];

  currentScenarios.forEach((scenario) => {
    if (!scenario.scenario_db_id) {
      // No db_id = new scenario
      result.scenarios.create.push(scenario);
    } else {
      // Has db_id - check if it exists in original
      const originalScenario = originalScenarios.find(
        (e) => e.scenario_db_id === scenario.scenario_db_id,
      );

      if (!originalScenario) {
        // Has db_id but not in original = new scenario
        result.scenarios.create.push(scenario);
      } else if (
        hasBeenModified(scenario, originalScenario, [
          'scenario_name',
          'scenario_type',
          'description',
          'matching_attribute_name',
          'matching_attribute_value',
        ])
      ) {
        // Existing scenario that was modified
        const changedFields = {};

        if (scenario.scenario_name !== originalScenario?.scenario_name) {
          changedFields.scenario_name = scenario.scenario_name;
        }
        if (scenario.scenario_type !== originalScenario?.scenario_type) {
          changedFields.scenario_type = scenario.scenario_type;
        }
        if (scenario.description !== originalScenario?.description) {
          changedFields.description = scenario.description;
        }
        if (scenario.matching_attribute_name !== originalScenario?.matching_attribute_name) {
          changedFields.matching_attribute_name = scenario.matching_attribute_name;
        }
        if (scenario.matching_attribute_value !== originalScenario?.matching_attribute_value) {
          changedFields.matching_attribute_value = scenario.matching_attribute_value;
        }

        result.scenarios.update.push({
          ...scenario,
          changedFields,
        });
      }
    }
  });

  // Find deleted scenarios
  originalScenarios.forEach((originalScenario) => {
    if (originalScenario.scenario_db_id) {
      const stillExists = currentScenarios.find(
        (e) => e.scenario_db_id === originalScenario.scenario_db_id,
      );
      if (!stillExists) {
        result.scenarios.delete.push(originalScenario);
      }
    }
  });

  // Process attributes for all scenarios
  currentScenarios.forEach((scenario, scenarioIndex) => {
    const currentAttributes = scenario.scenario_attributes || [];
    const originalScenario = originalScenarios.find(
      (e) =>
        (e.scenario_db_id && e.scenario_db_id === scenario.scenario_db_id) ||
        e.scenario_id === scenario.scenario_id,
    );
    const originalAttributes = originalScenario?.scenario_attributes || [];

    currentAttributes.forEach((attr) => {
      // Add scenario reference for API calls
      const attrWithScenarioRef = {
        ...attr,
        scenarioIndex,
        scenario_db_id: scenario.scenario_db_id,
        scenario_type: scenario.scenario_type,
      };

      // Schema-linked attributes never use insert/update-test-flow.
      // Only changed DYNAMIC values are saved, via /test-events/custom-values.
      if (attr.is_confluence_validation_schema_value) {
        if (attr.attribute_type === 'Dynamic') {
          const originalSchemaAttr = originalAttributes.find(
            (a) =>
              a.confluence_validation_schema_value_id ===
              attr.confluence_validation_schema_value_id,
          );
          if (!originalSchemaAttr || originalSchemaAttr.expected_value !== attr.expected_value) {
            result.customValues.push({
              direction: scenario.scenario_type === 'inbound' ? 'inbound' : 'outbound',
              scenario_id: scenario.scenario_db_id,
              confluence_validation_schema_value_id: attr.confluence_validation_schema_value_id,
              value: attr.expected_value,
            });
          }
        }
        return; // static schema attrs are read-only; never fall through to create/update
      }

      if (!attr.attribute_db_id) {
        // No db_id = new attribute
        result.attributes.create.push(attrWithScenarioRef);
      } else {
        // Has db_id - check if it exists in original
        const originalAttr = originalAttributes.find(
          (a) => a.attribute_db_id === attr.attribute_db_id,
        );

        if (!originalAttr) {
          // Has db_id but not in original = new attribute
          result.attributes.create.push(attrWithScenarioRef);
        } else if (
          hasBeenModified(attr, originalAttr, [
            'attribute_name',
            'expected_value',
            'attribute_type',
          ])
        ) {
          // Existing attribute that was modified
          const changedFields = {};

          if (attr.attribute_name !== originalAttr?.attribute_name) {
            changedFields.name = attr.attribute_name;
          }
          if (attr.expected_value !== originalAttr?.expected_value) {
            changedFields.expected = attr.expected_value; // Expected value goes to 'expected' field
          }
          if (attr.attribute_type !== originalAttr?.attribute_type) {
            changedFields.config_type = attr.attribute_type; // Static/Dynamic goes to 'config_type' field
          }

          result.attributes.update.push({
            ...attrWithScenarioRef,
            changedFields,
          });
        }
      }
    });

    // Find deleted attributes for this scenario
    if (originalScenario) {
      originalAttributes.forEach((originalAttr) => {
        if (originalAttr.attribute_db_id) {
          const stillExists = currentAttributes.find(
            (a) => a.attribute_db_id === originalAttr.attribute_db_id,
          );
          if (!stillExists) {
            result.attributes.delete.push({
              ...originalAttr,
              scenario_db_id: originalScenario.scenario_db_id,
              scenario_type: originalScenario.scenario_type,
            });
          }
        }
      });
    }
  });

  return result;
};

/**
 * Summarizes changes for user display
 */
export const summarizeChanges = (changes) => {
  const summary = [];

  if (changes.event?.operation === 'CREATE') {
    summary.push('Creating new event');
  } else if (changes.event?.operation === 'UPDATE') {
    summary.push('Updating event details');
  }

  if (changes.scenarios.create.length > 0) {
    summary.push(`Creating ${changes.scenarios.create.length} new scenario(s)`);
  }
  if (changes.scenarios.update.length > 0) {
    summary.push(`Updating ${changes.scenarios.update.length} scenario(s)`);
  }
  if (changes.scenarios.delete.length > 0) {
    summary.push(`Deleting ${changes.scenarios.delete.length} scenario(s)`);
  }

  if (changes.attributes.create.length > 0) {
    summary.push(`Creating ${changes.attributes.create.length} new attribute(s)`);
  }
  if (changes.attributes.update.length > 0) {
    summary.push(`Updating ${changes.attributes.update.length} attribute(s)`);
  }
  if (changes.attributes.delete.length > 0) {
    summary.push(`Deleting ${changes.attributes.delete.length} attribute(s)`);
  }

  if (changes.customValues && changes.customValues.length > 0) {
    summary.push(`Saving ${changes.customValues.length} schema value override(s)`);
  }

  return summary.length > 0 ? summary : ['No changes detected'];
};

/**
 * Checks if there are any changes that need to be saved
 */
export const hasChanges = (changes) => {
  return (
    changes.event?.operation !== 'NONE' ||
    changes.scenarios.create.length > 0 ||
    changes.scenarios.update.length > 0 ||
    changes.scenarios.delete.length > 0 ||
    changes.attributes.create.length > 0 ||
    changes.attributes.update.length > 0 ||
    changes.attributes.delete.length > 0 ||
    (changes.customValues && changes.customValues.length > 0)
  );
};

// TODO: Add more specific field comparisons once we know all the fields that can be edited
// TODO: Add support for comparing complex nested structures if needed
// TODO: Consider adding a "revert" function to restore original values
